import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Box, Container, TextField, Button, Typography, Alert, CircularProgress, Link } from '@mui/material';
import Layout from './components/Layout';
import LoginForm from './components/LoginForm';
import Home from './pages/Home';
import Providers from './pages/Providers';
import Clients from './pages/Clients';
import Visits from './pages/Visits';
import ISP from './pages/ISP';
import Register from './pages/Register';
import ForgotPassword from './components/ForgotPassword';
import ResetPassword from './components/ResetPassword';
import Billing from './pages/Billing';
import ComplianceReporting from './pages/ComplianceReporting';
import BillingAgents from './pages/BillingAgents';
import Transportation from './pages/Transportation';
import UnusualIncidents from './pages/UnusualIncidents';
import { authService } from './services/auth';
import { TokenManager } from './utils/tokenManager';
import SessionTimeoutNotification from './components/SessionTimeoutNotification';

interface User {
  username: string;
  attributes: {
    email: string;
    'custom:role': string;
    'custom:provider_id': string;
    'custom:user_id': string;
    'custom:admin_flag': string;
    'custom:billing_agent_flag': string;
    'custom:billing_agent_id': string;
    'custom:accessible_provider_ids': string;
    'custom:is_active': string;
    'custom:is_verified': string;
    given_name: string;
    family_name: string;
  };
}

const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#2D4B73', // Dark Blue for headers, buttons, fonts, text box outlines, footer icons
      light: '#4A6B94',
      dark: '#1E3452',
      contrastText: '#ffffff', // White text for better contrast on dark blue
    },
    secondary: {
      main: '#2D4B73', // Dark Blue accent
      light: '#4A6B94',
      dark: '#1E3452',
      contrastText: '#ffffff', // White text for better contrast on dark blue
    },
    background: {
      default: '#BFD3EA', // Light Blue/Grey-Blue for page background
      paper: '#ffffff',
    },
    text: {
      primary: '#2D4B73', // Dark Blue text for better readability
      secondary: '#2D4B73', // Dark Blue for secondary text
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontWeight: 700,
      color: '#2D4B73',
    },
    h2: {
      fontWeight: 600,
      color: '#2D4B73',
    },
    h3: {
      fontWeight: 600,
      color: '#2D4B73',
    },
    h4: {
      fontWeight: 600,
      color: '#2D4B73',
    },
    h5: {
      fontWeight: 600,
      color: '#2D4B73',
    },
    h6: {
      fontWeight: 600,
      color: '#2D4B73',
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          textTransform: 'none',
          fontWeight: 600,
          padding: '12px 24px',
        },
        containedPrimary: {
          backgroundColor: '#2D4B73',
          color: '#ffffff', // Explicit white text
          border: '2px solid #1E3452',
          boxShadow: '0 4px 14px 0 rgba(45, 75, 115, 0.4)',
          '&:hover': {
            backgroundColor: '#1E3452',
            color: '#ffffff', // Maintain white text on hover
            boxShadow: '0 6px 20px 0 rgba(45, 75, 115, 0.5)',
          },
        },
        containedSecondary: {
          backgroundColor: '#2D4B73',
          color: '#ffffff', // Explicit white text
          border: '2px solid #1E3452',
          boxShadow: '0 4px 14px 0 rgba(45, 75, 115, 0.4)',
          '&:hover': {
            backgroundColor: '#1E3452',
            color: '#ffffff', // Maintain white text on hover
            boxShadow: '0 6px 20px 0 rgba(45, 75, 115, 0.5)',
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            backgroundColor: 'rgba(255, 255, 255, 0.9)',
            borderRadius: 12,
            border: '1px solid rgba(45, 75, 115, 0.3)',
            '&:hover': {
              border: '1px solid rgba(45, 75, 115, 0.5)',
            },
            '&.Mui-focused': {
              border: '1px solid #2D4B73',
            },
          },
          '& .MuiInputLabel-root': {
            color: '#2D4B73',
            '&.Mui-focused': {
              color: '#2D4B73',
            },
          },
          '& .MuiOutlinedInput-input': {
            color: '#2D4B73',
            '&::placeholder': {
              color: 'rgba(45, 75, 115, 0.6)',
            },
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          backgroundColor: 'rgba(255, 255, 255, 0.1)',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          boxShadow: '0 8px 32px 0 rgba(139, 92, 246, 0.15)',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: '#2D4B73', // Dark Blue header
          boxShadow: '0 4px 20px 0 rgba(45, 75, 115, 0.3)',
        },
      },
    },
    MuiContainer: {
      styleOverrides: {
        root: {
          background: '#BFD3EA', // Light Blue/Grey-Blue background
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        },
      },
    },
  },
});

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [selectedProvider, setSelectedProvider] = useState<number | null>(null);

  useEffect(() => {
    checkAuth();
    
    // Cleanup token monitoring on unmount
    return () => {
      TokenManager.stopTokenMonitoring();
    };
  }, []);

  const canAccessPortal = (u: User) =>
    u.attributes['custom:admin_flag'] === 'true' ||
    u.attributes['custom:billing_agent_flag'] === 'true';

  const initialProviderForUser = (_u: User): number | null => {
    // Billing agents: Layout picks the default from a fresh providers API call.
    return null;
  };

  const checkAuth = async () => {
    try {
      console.log('Checking authentication...');
      let currentUser: User;
      try {
        currentUser = await authService.refreshUserProfile();
      } catch {
        currentUser = await authService.getCurrentUser();
      }
      console.log('Current user:', currentUser);
      
      if (!canAccessPortal(currentUser)) {
        console.log('User without admin portal access attempted login');
        setIsAuthenticated(false);
        setUser(null);
        TokenManager.stopTokenMonitoring();
        return;
      }
      
      setUser(currentUser);
      setSelectedProvider(initialProviderForUser(currentUser));
      setIsAuthenticated(true);
      
      TokenManager.startTokenMonitoring();
    } catch (err) {
      console.error('Authentication check failed:', err);
      setIsAuthenticated(false);
      // Stop token monitoring for unauthenticated users
      TokenManager.stopTokenMonitoring();
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignIn = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    try {
      console.log('Attempting sign in...', { email });
      const user = await authService.signIn(email, password);
      console.log('Sign in successful:', user);
      
      if (!canAccessPortal(user)) {
        console.log('User without admin portal access attempted login');
        setError('Access denied. Admin or billing agent privileges required.');
        setIsAuthenticated(false);
        setUser(null);
        return;
      }
      
      setUser(user);
      setSelectedProvider(initialProviderForUser(user));
      setIsAuthenticated(true);
      TokenManager.startTokenMonitoring();
    } catch (err) {
      console.error('Sign in failed:', err);
      setError(err instanceof Error ? err.message : 'An error occurred during sign in');
      setIsAuthenticated(false);
      TokenManager.stopTokenMonitoring();
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignOut = async () => {
    try {
      setIsLoading(true);
      console.log('Signing out...');
      TokenManager.stopTokenMonitoring();
      await authService.signOut();
      setIsAuthenticated(false);
      setUser(null);
      console.log('Sign out successful');
    } catch (err) {
      console.error('Sign out failed:', err);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading || isAuthenticated === null) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '100vh',
          }}
        >
          <CircularProgress />
        </Box>
      </ThemeProvider>
    );
  }

  if (!isAuthenticated) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Router>
          <Routes>
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="*" element={
              <LoginForm
                email={email}
                password={password}
                error={error}
                isLoading={isLoading}
                onEmailChange={setEmail}
                onPasswordChange={setPassword}
                onSubmit={handleSignIn}
              />
            } />
          </Routes>
        </Router>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <SessionTimeoutNotification isAuthenticated={isAuthenticated} />
      <Router>
        <Layout 
          signOut={handleSignOut}
          selectedProvider={selectedProvider}
          onProviderChange={setSelectedProvider}
          user={user}
        >
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/providers" element={<Providers />} />
            <Route path="/billing-agents" element={
              user?.attributes['custom:admin_flag'] === 'true'
                ? <BillingAgents />
                : <Navigate to="/" replace />
            } />
            <Route path="/clients" element={<Clients selectedProvider={selectedProvider} />} />
            <Route path="/visits" element={<Visits selectedProvider={selectedProvider} />} />
            <Route path="/isp" element={<ISP selectedProvider={selectedProvider} />} />
            <Route path="/billing" element={<Billing selectedProvider={selectedProvider} />} />
            <Route path="/compliance-reporting" element={<ComplianceReporting selectedProvider={selectedProvider} />} />
            <Route path="/transportation" element={<Transportation selectedProvider={selectedProvider} />} />
            <Route path="/unusual-incidents" element={<UnusualIncidents selectedProvider={selectedProvider} />} />
          </Routes>
        </Layout>
      </Router>
    </ThemeProvider>
  );
}

export default App; 